import * as React from 'react';
import { ArrowRight } from 'lucide-react';

import { cn } from '@shared/lib/utils';

const CardInteractiveContext = React.createContext(false);

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Opt-in visual state for a Card whose parent supplies navigation or an action. */
  interactive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, onClick, onKeyDown, role, tabIndex, ...props }, ref) => {
    const isInteractive = interactive ?? Boolean(onClick || role === 'button' || role === 'link');
    const resolvedRole = role ?? (onClick ? 'button' : undefined);
    const interactionProps: React.HTMLAttributes<HTMLDivElement> = {
      onClick,
      onKeyDown: (event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || onClick === undefined) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.currentTarget.click();
      },
      role: resolvedRole,
      tabIndex: tabIndex ?? (onClick ? 0 : undefined),
    };

    return (
      <CardInteractiveContext.Provider value={isInteractive}>
        <div
          ref={ref}
          className={cn(
            'rounded-none border bg-card text-card-foreground shadow-none',
            isInteractive &&
              'cursor-pointer transition-colors [@media(hover:hover)]:hover:bg-[hsl(var(--brand-accent-tint))] active:bg-[hsl(var(--brand-accent-tint))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className
          )}
          data-interactive={isInteractive || undefined}
          {...interactionProps}
          {...props}
        />
      </CardInteractiveContext.Provider>
    );
  }
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

interface CardHeaderRowProps extends React.HTMLAttributes<HTMLDivElement> {
  controls?: React.ReactNode;
}

const CardHeaderRow = React.forwardRef<HTMLDivElement, CardHeaderRowProps>(
  ({ children, className, controls, ...props }, ref) => {
    const isInteractive = React.useContext(CardInteractiveContext);
    return (
      <div
        ref={ref}
        data-slot="card-header-row"
        className={cn('flex min-w-0 items-center justify-between gap-2', className)}
        {...props}
      >
        <div data-slot="card-header-title" className="flex min-w-0 flex-1 items-center">
          {children}
        </div>
        {controls || isInteractive ? (
          <div data-slot="card-header-controls" className="flex shrink-0 items-center gap-2">
            {controls}
            {isInteractive ? (
              <ArrowRight
                aria-hidden="true"
                data-slot="card-navigation-indicator"
                className="size-6 shrink-0 text-card-foreground"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
);
CardHeaderRow.displayName = 'CardHeaderRow';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardHeaderRow, CardFooter, CardTitle, CardDescription, CardContent };
